import { Injectable, inject } from '@angular/core';
import { faker } from '@faker-js/faker';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, take, tap, withLatestFrom } from 'rxjs';
import { IStudent } from 'src/app/core/models/user';
import { mapAbsenteeRecordsToIAttendanceRecords } from '../../graphql/functions/mappingFunctions';
import { AnnouncementsService } from '../../services/communication/announcements.service';
import { NotificationService } from '../../services/notifications/notifications.service';
import { StudentService } from '../../services/student/student.service';
import { selectLoggedInGuardianLearnerAccess } from '../guardian/guardian.selectors';
import { NotificationActions } from '../notifications/notification.actions';
import { ResourceActions } from '../resources/resource.actions';
import { selectStudentHasReports } from '../resources/resource.selectors';
import { StaffActions } from '../staff/staff.actions';
import { StudentActions } from './student.actions';
import { selectSelectedStudent, selectSelectedStudentId } from './student.selectors';
import { ImageCacheActions } from '../image-cache/image-cache.actions';
import { EventActions } from '../events/event.actions';

@Injectable()
export class StudentEffects {
  // Use inject() for newly added dependency (best practice)
  private readonly announcementsService = inject(AnnouncementsService);
  // Track in-flight picture updates to distinguish optimistic vs committed success without new actions
  private inFlightPictureUpdates: Record<string, { committed: boolean }> = {};

  // Helper extracted to reduce nesting depth in effects
  private mapEmergencyContactRelationship(
    contact: any,
    relationshipId: string,
  ) {
    const existingRel =
      contact?.relationship && typeof contact.relationship === 'object'
        ? contact.relationship
        : {};
    return {
      ...contact,
      relationship: {
        ...existingRel,
        id: relationshipId,
      },
    };
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private studentService: StudentService,
    private notificationService: NotificationService,
  ) {}

  loadRealStudent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.loadStudents),
      withLatestFrom(this.store.select(selectLoggedInGuardianLearnerAccess)),
      mergeMap(([_, studentIds]) => {
        return this.studentService.getStudents(studentIds).pipe(
          tap(({ errors }) => {
            // Capture and optionally log errors if needed – removed unused variable assignment.
            if (errors?.length) {
              // Optionally log or send to monitoring service
              // console.debug('Student load errors:', errors.map(e => e.message).filter(m => !!m));
            }
          }),
          map(({ students, errors }) => {
            if (students && students.length > 0) {
              return StudentActions.loadStudentsSuccess({ students });
            }

            const fallbackError = errors?.[0]?.message ?? 'No students found';
            return StudentActions.loadStudentsFailure({ error: fallbackError });
          }),
          catchError((error) => {
            console.error('Error loading students:', error);
            return of(
              StudentActions.loadStudentsFailure({
                error:
                  error instanceof Error
                    ? error.message
                    : 'Unknown error occurred',
              }),
            );
          }),
        );
      }),
    ),
  );

  loadStudentsForAccesschange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.loadStudentsForAccesschange),
      mergeMap(({ studentIds }) =>
        this.studentService.getStudentsForAccessChange(studentIds).pipe(
          map(({ students, errors }) => {
            if (students && students.length > 0) {
              return StudentActions.loadStudentsForAccesschangeSuccess({ students });
            }

            const fallbackError = errors?.[0]?.message ?? 'No students found for access change';
            return StudentActions.loadStudentsForAccesschangeFailure({ error: fallbackError });
          }),
          catchError((error) =>
            of(
              StudentActions.loadStudentsForAccesschangeFailure({
                error: error instanceof Error ? error.message : 'Unknown error occurred',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  selectStudent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.selectStudent),
      map(() => StaffActions.loadStaff()),
    ),
  );

  loadSelectedStudentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.selectStudent),
      map((action) => action.id),
      mergeMap((studentId) => {
        // Use the existing selector to check if reports exist
        return this.store.select(selectStudentHasReports(studentId)).pipe(
          take(1),
          map((hasReports) => {
            // Only load reports if none exist for this student
            if (!hasReports) {
              return ResourceActions.loadStudentReports({ studentId });
            }

            // If reports already exist, return a "no-op" action
            return { type: '[Student] Reports Already Loaded' };
          }),
        );
      }),
    ),
  );

  sendNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.addStudent), // Listen for the addStudent action
      map((action) => {
        const student = action.student; // Extract student data from action
        const notification = {
          id: faker.string.uuid(), // Generate a unique ID
          learnerName: student.firstname,
          relatedIds: [{ identifier: student.id, name: 'student' }],
          read: false,
          colour: student.colour, // Use the student's color
          icon: 'check', // Set a default icon or randomize if needed
          message: ` is now enrolled`, // Notification message
          timestamp: new Date(), // Current time
          url: `/enrolments/status`, // URL for the notification
        };

        return NotificationActions.addNotification({ notification }); // Dispatch addNotification action
      }),
    ),
  );

  updateStudentPicture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.updateStudentPicture),
      mergeMap((action) =>
        this.store.select(selectSelectedStudent).pipe(
          take(1),
          mergeMap((student) => {
            if (!student) {
              return of(
                StudentActions.updateStudentFailure({
                  error: 'No student selected',
                }),
              );
            }

            const { studentid, pictureFile, pictureId, pictureUrl } = action;
            const fileName = studentid;
            const prevPictureId = student.pictureId ?? null;
            const prevPictureUrl =
              typeof student.pictureUrl === 'string' ? student.pictureUrl : '';

            // Record in-flight state
            this.inFlightPictureUpdates[student.id] = { committed: false };

            // Optimistic update
            const optimisticStudent: IStudent = {
              ...student,
              pictureUrl: pictureFile ? pictureUrl : '',
              pictureId: !pictureFile && pictureId ? null : student.pictureId,
            } as IStudent;
            this.store.dispatch(
              StudentActions.updateStudentSuccess({
                student: optimisticStudent,
              }),
            );

            // Delete case
            if (!pictureFile && pictureId) {
              return this.studentService.deleteProfileImage(pictureId).pipe(
                map(() => {
                  this.inFlightPictureUpdates[student.id].committed = true;
                  // Dispatch success again to trigger snackbar
                  this.store.dispatch(
                    StudentActions.updateStudentSuccess({
                      student: optimisticStudent,
                    }),
                  );
                  delete this.inFlightPictureUpdates[student.id];

                  this.store.dispatch(
                    ImageCacheActions.profileImageUpdated({
                      userId: student.id,
                      oldImageUrl:
                        typeof student.pictureUrl === 'string'
                          ? student.pictureUrl
                          : undefined,
                      newImageUrl: pictureFile ? pictureUrl : '',
                    }),
                  );

                  this.notificationService.showSnackBar({
                    message: 'Success! Profile picture updated.',
                    type: 'success',
                  });

                  return { type: '[Student] Picture Delete Confirmed' };
                }),
                catchError((error) => {
                  const revertStudent = {
                    ...student,
                    pictureUrl: prevPictureUrl,
                    pictureId: prevPictureId,
                  } as IStudent;
                  this.store.dispatch(
                    StudentActions.updateStudentSuccess({
                      student: revertStudent,
                    }),
                  );
                  delete this.inFlightPictureUpdates[student.id];

                  return of(StudentActions.updateStudentFailure({ error }));
                }),
              );
            }

            // Upload/update case
            if (pictureFile) {
              return this.studentService
                .updateProfileImage(
                  pictureFile,
                  fileName,
                  studentid,
                  pictureId ?? '',
                )
                .pipe(
                  map((response) => {
                    this.inFlightPictureUpdates[student.id].committed = true;
                    let finalStudent = optimisticStudent;
                    if (
                      response.id &&
                      response.id !== optimisticStudent.pictureId
                    ) {
                      finalStudent = {
                        ...optimisticStudent,
                        pictureId: response.id,
                      } as IStudent;
                    }
                    this.store.dispatch(
                      StudentActions.updateStudentSuccess({
                        student: finalStudent,
                      }),
                    );
                    delete this.inFlightPictureUpdates[student.id];

                    this.store.dispatch(
                      ImageCacheActions.profileImageUpdated({
                        userId: student.id,
                        oldImageUrl:
                          typeof student.pictureUrl === 'string'
                            ? student.pictureUrl
                            : undefined,
                        newImageUrl: pictureFile ? pictureUrl : '',
                      }),
                    );

                    this.notificationService.showSnackBar({
                      message: 'Success! Profile picture updated.',
                      type: 'success',
                    });

                    return { type: '[Student] Picture Upload Confirmed' };
                  }),
                  catchError((error) => {
                    const revertStudent = {
                      ...student,
                      pictureUrl: prevPictureUrl,
                      pictureId: prevPictureId,
                    } as IStudent;
                    this.store.dispatch(
                      StudentActions.updateStudentSuccess({
                        student: revertStudent,
                      }),
                    );
                    delete this.inFlightPictureUpdates[student.id];
                    return of(StudentActions.updateStudentFailure({ error }));
                  }),
                );
            }

            // Invalid
            delete this.inFlightPictureUpdates[student.id];
            return of(
              StudentActions.updateStudentFailure({
                error: 'Invalid picture operation',
              }),
            );
          }),
        ),
      ),
    ),
  );

  updatePreferredName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.updatePersonalInfo),
      withLatestFrom(this.store.select(selectSelectedStudentId)),
      mergeMap(([action, selectedStudentId]) => {
        const preferredName = action.preferredName;
        if (!selectedStudentId) {
          return of({ type: '[Student] No Action' });
        }
        return this.studentService
          .updatePreferredName(selectedStudentId, preferredName)
          .pipe(
            mergeMap(() =>
              this.store.select(selectSelectedStudent).pipe(
                take(1),
                map((selectedStudent) => {
                  if (!selectedStudent) return { type: '[Student] No Action' };
                  const updatedStudent = {
                    ...selectedStudent,
                    preferredName,
                  } as IStudent;
                  this.notificationService.showSnackBar({
                    message: 'Success! Personal information updated.',
                    type: 'success',
                  });
                  return StudentActions.updateStudentSuccess({
                    student: updatedStudent,
                  });
                }),
              ),
            ),
            catchError((error) =>
              of(StudentActions.updateStudentFailure({ error })),
            ),
          );
      }),
    ),
  );

  updateEmergencyContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.updateEmergencyContact),
      withLatestFrom(this.store.select(selectSelectedStudentId)),
      mergeMap(([action, selectedStudentId]) => {
        const updatedEmergencyContact = action.contact;
        if (!selectedStudentId) {
          return of({ type: '[Student] No Action' });
        }
        return this.studentService
          .updateEmergencyContact(selectedStudentId, updatedEmergencyContact)
          .pipe(
            mergeMap(() =>
              this.store.select(selectSelectedStudent).pipe(
                take(1),
                map((selectedStudent) => {
                  if (!selectedStudent) return { type: '[Student] No Action' };
                  const relationshipId =
                    typeof updatedEmergencyContact.relationship === 'string'
                      ? updatedEmergencyContact.relationship
                      : (updatedEmergencyContact.relationship?.id ?? '');
                  const updatedStudent: IStudent = {
                    ...selectedStudent,
                    emergencyContacts:
                      selectedStudent.emergencyContacts?.length > 0
                        ? [
                            this.mapEmergencyContactRelationship(
                              {
                                ...selectedStudent.emergencyContacts[0],
                                ...updatedEmergencyContact,
                              },
                              relationshipId,
                            ),
                            ...selectedStudent.emergencyContacts.slice(1),
                          ]
                        : [
                            this.mapEmergencyContactRelationship(
                              updatedEmergencyContact,
                              relationshipId,
                            ),
                          ],
                  } as IStudent;
                  this.notificationService.showSnackBar({
                    message: 'Success! Emergency contact updated.',
                    type: 'success',
                  });
                  return StudentActions.updateStudentSuccess({
                    student: updatedStudent,
                  });
                }),
              ),
            ),
            catchError((error) =>
              of(StudentActions.updateStudentFailure({ error })),
            ),
          );
      }),
    ),
  );

  updateMedicalInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.updateMedicalInfo),
      withLatestFrom(this.store.select(selectSelectedStudentId)), // Get selected student ID
      mergeMap(([action, selectedStudentId]) => {
        const updatedMedicalInfo = action.medicalInfo;

        if (!selectedStudentId) {
          return of({ type: '[Student] No Action' }); // Prevent updating if no student is selected
        }

        // Update it on the server
        return this.studentService
          .updateMedicalInformation(selectedStudentId, updatedMedicalInfo)
          .pipe(
            // If server update succeeds, then update the local store
            mergeMap(() =>
              this.store.select(selectSelectedStudent).pipe(
                take(1),
                map((selectedStudent) => {
                  if (!selectedStudent) return { type: '[Student] No Action' };

                  const updatedStudent = {
                    ...selectedStudent,
                    medicalInfo: { ...updatedMedicalInfo },
                  };

                  this.notificationService.showSnackBar({
                    message: 'Success! Medical information updated.',
                    type: 'success',
                  });
                  return StudentActions.updateStudentSuccess({
                    student: updatedStudent,
                  });
                }),
              ),
            ),
            catchError((error) =>
              of(StudentActions.updateStudentFailure({ error })),
            ),
          );
      }),
    ),
  );

  updateStudentFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.updateStudentFailure),
      map(() => {
        this.notificationService.showSnackBar({
          message: 'Something went wrong. Please try again.',
          type: 'error',
        });
        return { type: '[Student] Notification Shown' };
      }),
    ),
  );

  updateStudentPictureStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.updateStudentPicture),
      map(() => {
        return { type: '[Student] Picture Update Started' };
      }),
    ),
  );

  // Removed commit/rollback specific snackbars; generic success/failure remain.

  loadStudentAttendance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentActions.loadStudentAttendance),
      mergeMap((action) => {
        const { studentId } = action;
        return this.studentService.getAbsenteeRecords(studentId).pipe(
          map((response) => {
            const absenteeRecords =
              response.data?.studentSearch?.content?.[0]?.allocations?.[0]
                ?.studentYear?.absenteeRecords || [];
            const attendance =
              mapAbsenteeRecordsToIAttendanceRecords(absenteeRecords);
            return StudentActions.loadStudentAttendanceSuccess({
              studentId,
              attendance,
            });
          }),
          catchError((error) => {
            console.error('Error loading student attendance:', error);
            return of(
              StudentActions.loadStudentAttendanceFailure({
                studentId,
                error:
                  error instanceof Error
                    ? error.message
                    : 'Unknown error occurred',
              }),
            );
          }),
        );
      }),
    ),
  );

  // After students load successfully, load their communications and unread count
  loadCommunicationsAfterStudentsLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StudentActions.loadStudentsSuccess),
        map((action) => action.students.map((s) => s.id)),
        tap((studentIds) => {
          this.announcementsService.loadUnreadCount(studentIds);
          this.announcementsService.loadAnnouncements(studentIds);
        }),
      ),
    { dispatch: false },
  );

  setEvents$ = createEffect(() => this.actions$.pipe(
      ofType(StudentActions.loadStudentsSuccess),
      map(() => EventActions.loadEvents())
  ));
}
